import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { gql, useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, Col, DatePicker, Form, Input, Row, Select, Typography } from 'antd'
import { ClearOutlined, FilterOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import { DATE_FORMAT, ORDER_STATES, SORTS } from './constants'

const { RangePicker } = DatePicker

const PROVINCES_QUERY = gql`
  query LineItemsFilterProvinces {
    provinces {
      id
      name
    }
  }
`

const DISTRICTS_QUERY = gql`
  query LineItemsFilterDistricts($provinceId: ID!) {
    districts(provinceId: $provinceId) {
      id
      name
    }
  }
`

const camelize = (value) => value.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase())

const filterOptionByLabel = (input, option) => (
  (option && option.label ? String(option.label) : '').toLowerCase().includes(input.toLowerCase())
)

const toRange = (from, to) => (from && to ? [dayjs(from, DATE_FORMAT), dayjs(to, DATE_FORMAT)] : null)

const Filters = ({ filters, resolvedItem, onApply, onClear }) => {
  const { t } = useTranslation('lineItems')
  const [draft, setDraft] = useState(filters)

  const provincesQuery = useQuery(PROVINCES_QUERY)
  const districtsQuery = useQuery(DISTRICTS_QUERY, {
    variables: { provinceId: draft.provinceId },
    skip: !draft.provinceId
  })

  const update = (changes) => setDraft((current) => ({ ...current, ...changes }))

  const provinceOptions = ((provincesQuery.data && provincesQuery.data.provinces) || [])
    .map((province) => ({ value: province.id, label: province.name }))
  const districtOptions = ((draft.provinceId && districtsQuery.data && districtsQuery.data.districts) || [])
    .map((district) => ({ value: district.id, label: district.name }))

  const handleRangeChange = (range) => {
    update({
      paidFrom: range && range[0] ? range[0].format(DATE_FORMAT) : undefined,
      paidTo: range && range[1] ? range[1].format(DATE_FORMAT) : undefined
    })
  }

  const handleSubmit = () => {
    onApply({ ...draft, query: draft.query.trim(), item: draft.item.trim() })
  }

  return (
    <Form layout='vertical' onFinish={handleSubmit} style={{ marginBottom: 8 }}>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item label={t('filters.query')}>
            <Input
              allowClear
              value={draft.query}
              placeholder={t('filters.queryPlaceholder')}
              onChange={(event) => update({ query: event.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label={t('filters.item')}
            extra={resolvedItem && draft.item === filters.item ? (
              <Typography.Text type='secondary'>{resolvedItem.name}</Typography.Text>
            ) : null}
          >
            <Input
              allowClear
              value={draft.item}
              placeholder={t('filters.itemPlaceholder')}
              onChange={(event) => update({ item: event.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('filters.paidRange')}>
            <RangePicker
              style={{ width: '100%' }}
              value={toRange(draft.paidFrom, draft.paidTo)}
              onChange={handleRangeChange}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('filters.province')}>
            <Select
              allowClear
              showSearch
              value={draft.provinceId}
              placeholder={t('filters.provincePlaceholder')}
              loading={provincesQuery.loading}
              options={provinceOptions}
              filterOption={filterOptionByLabel}
              onChange={(provinceId) => update({ provinceId, districtId: undefined })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('filters.district')}>
            <Select
              allowClear
              showSearch
              value={draft.districtId}
              placeholder={t('filters.districtPlaceholder')}
              disabled={!draft.provinceId && !draft.districtId}
              loading={districtsQuery.loading}
              options={districtOptions}
              filterOption={filterOptionByLabel}
              onChange={(districtId) => update({ districtId })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t('filters.sort')}>
            <Select
              value={draft.sort}
              options={SORTS.map((sort) => ({ value: sort, label: t(`sorts.${camelize(sort)}`) }))}
              onChange={(sort) => update({ sort })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={16}>
          <Form.Item label={t('filters.orderStates')} extra={t('filters.orderStatesHelp')}>
            <Select
              mode='multiple'
              allowClear
              value={draft.orderStates}
              options={ORDER_STATES.map((state) => ({ value: state, label: t(`states.${camelize(state)}`) }))}
              onChange={(orderStates) => update({ orderStates })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label=' '>
            <Checkbox
              checked={draft.requestItemsOnly}
              onChange={(event) => update({ requestItemsOnly: event.target.checked })}
            >
              {t('filters.requestItemsOnly')}
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col>
          <Button type='primary' htmlType='submit' icon={<FilterOutlined />}>
            {t('filters.apply')}
          </Button>
        </Col>
        <Col>
          <Button icon={<ClearOutlined />} onClick={onClear}>
            {t('filters.clear')}
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

Filters.propTypes = {
  filters: PropTypes.shape({
    query: PropTypes.string.isRequired,
    item: PropTypes.string.isRequired,
    provinceId: PropTypes.string,
    districtId: PropTypes.string,
    orderStates: PropTypes.arrayOf(PropTypes.string).isRequired,
    requestItemsOnly: PropTypes.bool.isRequired,
    paidFrom: PropTypes.string,
    paidTo: PropTypes.string,
    sort: PropTypes.string.isRequired
  }).isRequired,
  resolvedItem: PropTypes.shape({
    itemId: PropTypes.string,
    name: PropTypes.string
  }),
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired
}

Filters.defaultProps = {
  resolvedItem: null
}

export default Filters
